import { useEffect, useMemo, useState } from "preact/hooks";
import { Modal } from "./Modal";
import { idx, resolved, analysis, planTerm, flash } from "../../store";
import { active, settings } from "../../lib/state";
import { buildIcs } from "../../lib/ics";
import { teachersOf } from "../CourseView";
import { renderScheduleSvg, type RenderOptions } from "../../lib/render/svg";
import { THEMES, THEME_IDS } from "../../lib/render/themes";
import { downloadPdf, downloadPng, downloadSvg, printSvg, openPrintWindow } from "../../lib/render/export";

type Paper = RenderOptions["paper"];
const PAPERS: [Paper, string][] = [["a4-landscape", "A4 položeno"], ["a4-portrait", "A4 uspravno"], ["a5-landscape", "A5 džepni"], ["phone", "Pozadina za telefon"]];

function download(name: string, text: string, type: string) {
  const a = Object.assign(document.createElement("a"), { href: URL.createObjectURL(new Blob([text], { type })), download: name });
  a.click();
  setTimeout(() => URL.revokeObjectURL(a.href), 2000);
}

export function ExportDialog({ onClose }: { onClose: () => void }) {
  const I = idx.value!;
  const sched = active.value;
  const { slots } = resolved.value;
  const [theme, setTheme] = useState<RenderOptions["theme"]>("ledger");
  const [paper, setPaper] = useState<Paper>("a4-landscape");
  const [show, setShow] = useState({ room: true, teacher: false, group: true, weeks: false, legend: true });
  const [busy, setBusy] = useState(false);
  const legend = useMemo(() => sched.picks.map((p, i) => {
    const c = I.courses.get(p.id);
    if (!c || (c.term && c.term !== planTerm.value)) return null;
    const color = slots.find((s) => s.courseId === c.id)?.color ?? "#555";
    const t = teachersOf(c).filter((l) => l.email && l.email_verified !== false).slice(0, 2).map((l) => `${l.name} · ${l.email}`);
    return { title: p.label || c.name, color, lines: [`${c.fac}${c.ects ? ` · ${c.ects} ECTS` : ""}`, ...t] };
  }).filter(Boolean) as RenderOptions["legend"], [sched, slots]);
  const opts: RenderOptions = {
    theme, paper, title: sched.name, subtitle: `${planTerm.value === "winter" ? "Zimski" : "Ljetni"} semestar ${I.core.calendar.academic_year}`,
    showRoom: show.room, showTeacher: show.teacher, showGroup: show.group, showWeeks: show.weeks, clock: settings.value.clock,
    legend: show.legend ? legend : undefined, clashIds: analysis.value.clashIds, lang: settings.value.lang, footer: "slusam · nezvanični raspored",
  };
  const svg = useMemo(() => renderScheduleSvg(slots, opts), [slots, theme, paper, show, legend]);
  const base = sched.name.replace(/[^\p{L}\p{N}]+/gu, "-").toLowerCase() || "raspored";
  const run = async (fn: () => Promise<unknown> | unknown) => { setBusy(true); try { await fn(); } catch (e) { flash(`Greška: ${(e as Error).message}`); } finally { setBusy(false); } };
  const ics = () => download(`${base}-${planTerm.value}.ics`, buildIcs(slots, {
    term: planTerm.value, units: I.units, calendar: I.core.calendar, name: sched.name, domain: location.host,
    cycleOf: (s) => (s.courseId ? I.courses.get(s.courseId)?.cycle : 1),
  }), "text/calendar");
  const csv = () => download(`${base}.csv`, ["Dan,Od,Do,Predmet,Tip,Grupa,Sala,Nastavnik", ...slots.map((s) => [s.day, s.start, s.end, s.title, s.kind, s.group, s.room, s.lecturer].map((v) => `"${String(v ?? "").replace(/"/g, '""')}"`).join(","))].join("\n"), "text/csv");
  useEffect(() => { if (paper === "phone" && theme === "compact") setTheme("ledger"); }, [paper]);

  return (
    <Modal title="Izvoz rasporeda" onClose={onClose} wide>
      <div class="formrow">
        <div><div class="note" style="margin-bottom:.3rem">Stil</div>
          <div class="themes">{THEME_IDS.map((id) => <button key={id} class={theme === id ? "on" : ""} onClick={() => setTheme(id)}>{THEMES[id].label[settings.value.lang]}</button>)}</div></div>
        <label>Format<select class="field" value={paper} onChange={(e) => setPaper((e.target as HTMLSelectElement).value as Paper)}>{PAPERS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}</select></label>
      </div>
      <div class="formrow">
        {([["room", "sale"], ["teacher", "nastavnici"], ["group", "grupe"], ["weeks", "sedmice"], ["legend", "legenda s emailovima"]] as const).map(([k, l]) => (
          <label key={k} class="check" style="flex-direction:row;align-items:center;gap:.3rem;font:13px var(--body)"><input type="checkbox" checked={show[k]} onChange={(e) => setShow({ ...show, [k]: (e.target as HTMLInputElement).checked })} /> {l}</label>
        ))}
      </div>
      <div class="preview" dangerouslySetInnerHTML={{ __html: svg }} />
      <div class="row" style="margin-top:.8rem">
        <button class="btn" disabled={busy} onClick={() => run(() => downloadPdf(svg, { paper, filename: `${base}.pdf` }))}>PDF</button>
        <button class="btn ghost" disabled={busy} onClick={() => run(() => downloadPng(svg, { filename: `${base}.png`, paper, share: paper === "phone" }))}>PNG</button>
        <button class="btn ghost" disabled={busy} onClick={() => { const w = openPrintWindow(); run(() => printSvg(svg, paper, w)); }}>Printaj</button>
        <button class="btn ghost" onClick={() => downloadSvg(svg, `${base}.svg`)}>SVG</button>
        <span style="flex:1" />
        <button class="btn" onClick={ics}>Kalendar (.ics)</button>
        <button class="btn ghost" onClick={csv}>CSV</button>
      </div>
      <p class="note">.ics radi s Google, Apple i Outlook kalendarom. Državni praznici i sedmice bez nastave (npr. online sedmice na EFSA) su izostavljeni. Za kalendar koji se sam ažurira koristi Podijeli, pa Objavi.</p>
    </Modal>
  );
}
